import React, { useState, useRef, useEffect } from "react";
import styles from "./newMessageModal.module.css";
import axios from "axios";
import { FaMicrophone, FaStop, FaTrash } from "react-icons/fa";

const apiUrl = process.env.REACT_APP_URL;

const NewMessageModal = ({
  show,
  onClose,
  onSendMessage,
  receiverUsername,
}) => {
  /* The New Message Modal is used to send a text message or a voice message to another user. The component is used on the Pals page and on the Admin page. */

  const [messageInput, setMessageInput] = useState("");
  const [isTextMode, setIsTextMode] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioBlobRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingIntervalRef = useRef(null);
  const [maxChars, setMaxChars] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(0);
  const [feedbackMsg, setFeedbackMsg] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  /**
   * Get the maximum length of a text message and the maximum length of a voice message.*/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await axios.get(`${apiUrl}/messages/limits`, {
          withCredentials: true,
          params: {},
        });

        if (settingsRes.data && settingsRes.data.success) {
          const fetchedSettings = settingsRes.data.data[0];

          setMaxChars(fetchedSettings.max_text_message_length);
          setMaxSeconds(fetchedSettings.max_voice_message_length);
        }
      } catch (error) {
        console.error("Failed to fetch maximum length settings: ", error);
      }
    };
    fetchData();
  }, []);

  /**
   * Toggle between text message and voice message
   */
  const handleToggleMode = () => {
    setIsTextMode((prevMode) => !prevMode);
    setFeedbackMsg(null); // Reset warning when toggling modes
  };

  /**
   * Start recording a voice message
   */
  const handleStartRecording = async () => {
    try {
      setFeedbackMsg(null); // Hide warning when starting a recording
      setAudioUrl(null);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      recorder.onstop = handleStop;

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Update the length of the recording every second
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting voice recording:", error);
    }
  };

  /**
   * Stop the recording of a voice message
   */
  const handleStopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const handleStop = () => {
    if (audioChunksRef.current.length === 0) {
      console.error("No audio data recorded");
      return;
    }
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    audioBlobRef.current = audioBlob;
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    audioChunksRef.current = [];
  };

  /**
   * Delete a voice message recording
   */
  const handleDeleteRecording = () => {
    setAudioUrl(null);
    audioChunksRef.current = [];
    audioBlobRef.current = null;
  };

  /**
   * Send a message to a user
   */
  const handleSend = () => {
    setFeedbackMsg(null);

    // Check if messageInput is empty in text mode or if no audio is recorded in voice mode, in which case the message will not be sent, and an error message will be shown.
    if (
      (isTextMode && messageInput.trim() === "") ||
      (!isTextMode && !audioBlobRef.current)
    ) {
      setFeedbackMsg("Message cannot be empty.");
      return;
    }

    if (isTextMode && messageInput.length > maxChars) {
      setFeedbackMsg("Message exceeds the maximum character limit.");
      return;
    }

    if (!isTextMode && recordingTime > maxSeconds) {
      setFeedbackMsg("Recording exceeds the maximum time limit.");
      return;
    }

    if (isTextMode) {
      onSendMessage({
        type: "text",
        content: messageInput,
        receiverUsername,
      });
      setMessageInput("");
    } else {
      onSendMessage({
        type: "audio",
        content: audioBlobRef.current,
        receiverUsername,
      });
      handleDeleteRecording();
    }

    // Show success message
    setShowSuccessMessage(true);
  };

  /* Once a success message has been displayed (after a successful sending of a message), prepare to close the modal after 1 second. */
  useEffect(() => {
    if (showSuccessMessage) {
      setTimeout(() => handleClose(), 1000);
    }
  }, [showSuccessMessage]);

  /**
   * Convert a number of seconds to a string with equivalent minutes and seconds
   * @param {*} seconds - the number of seconds to convert
   * @returns a string in the format mm:ss (minutes: seconds)
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  /**
   * Close the new message modal
   */
  const handleClose = () => {
    setShowSuccessMessage(false); // Reset the success message
    onClose();
    setFeedbackMsg(null); // Reset the warning message when modal closes
    setIsTextMode(true); // Reset to text mode when modal closes
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {showSuccessMessage ? (
          <div className={styles.successMessage}>
            Message sent successfully!
          </div>
        ) : (
          <>
            <div className={styles.switchTypeContainer}>
              <button
                className={styles.switchTypeButton}
                onClick={handleToggleMode}
              >
                {isTextMode ? "Switch to Voice" : "Switch to Text"}
              </button>
            </div>
            {isTextMode ? (
              <textarea
                className={styles.textInput}
                placeholder="Type your message here..."
                value={messageInput}
                onChange={(e) => {
                  setMessageInput(e.target.value);
                  setFeedbackMsg(null);
                }}
              />
            ) : (
              <>
                <div className={styles.recordingStatus}>
                  <span>Recording{isRecording ? "..." : ""}</span>
                  <span>{formatTime(recordingTime)}</span>
                </div>
                <button
                  className={styles.recordButton}
                  onClick={
                    isRecording ? handleStopRecording : handleStartRecording
                  }
                >
                  {isRecording ? <FaStop /> : <FaMicrophone />}
                </button>
              </>
            )}
            {audioUrl && (
              <div className={styles.audioPlayer}>
                <audio controls src={audioUrl}></audio>

                <button
                  className={styles.cancelButton}
                  onClick={handleDeleteRecording}
                >
                  <FaTrash />
                </button>
              </div>
            )}

            {feedbackMsg && <p className={styles.warningText}>{feedbackMsg}</p>}
            <div className={styles.buttonContainer}>
              <button className={styles.cancelButton} onClick={handleClose}>
                Cancel
              </button>
              <button className={styles.sendButton} onClick={handleSend}>
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NewMessageModal;
